import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Download, RefreshCw, X, AlertCircle, CheckCircle } from 'lucide-react';
import { isTauri, relaunchApp } from '../utils/tauri';

type UpdateStatus = 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'error';

const CHECK_INTERVAL = 30 * 60 * 1000; // 30분

export const UpdateNotification: React.FC = () => {
  const [status, setStatus] = useState<UpdateStatus>('idle');
  const [version, setVersion] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const updateRef = useRef<any>(null);

  const checkForUpdate = useCallback(async () => {
    if (!isTauri()) return;
    try {
      const { check } = await import('@tauri-apps/plugin-updater');
      const update = await check();
      if (update) {
        updateRef.current = update;
        setVersion(update.version);
        setStatus('available');
        setDismissed(false);
      }
    } catch (err: any) {
      console.error('Update check failed:', err);
    }
  }, []);

  useEffect(() => {
    if (!isTauri()) return;
    const initTimeout = setTimeout(checkForUpdate, 5000);
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL);
    return () => {
      clearTimeout(initTimeout);
      clearInterval(interval);
    };
  }, [checkForUpdate]);

  const handleInstall = async () => {
    if (!updateRef.current) return;
    try {
      setStatus('downloading');
      await updateRef.current.downloadAndInstall();
      setStatus('ready');
      await relaunchApp();
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || '업데이트 설치 실패');
      setTimeout(() => setDismissed(true), 5000);
    }
  };

  if (!isTauri() || status === 'idle' || status === 'checking' || dismissed) {
    return null;
  }

  return (
    // 흐름 안 배너 (Tauri 타이틀바 아래) — PwaInstallBanner와 top-9/z-50로 겹치던 문제 해소
    <div role="region" aria-label="업데이트 안내" className="shrink-0 mx-3 mt-2 md:mx-auto md:w-96 rounded-xl shadow-sm border overflow-hidden animate-slide-down">
      <div className={`px-4 py-3 ${
        status === 'error'
          ? 'bg-brand-50 border-brand-200'
          : status === 'ready'
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            {status === 'error' ? (
              <AlertCircle size={18} className="text-brand-600 shrink-0" />
            ) : status === 'ready' ? (
              <CheckCircle size={18} className="text-green-500 shrink-0" />
            ) : status === 'downloading' ? (
              <RefreshCw size={18} className="text-brand-600 shrink-0 animate-spin" />
            ) : (
              <Download size={18} className="text-brand-600 shrink-0" />
            )}
            <span className="text-sm font-semibold text-gray-900">
              {status === 'error' ? '업데이트 실패' : status === 'ready' ? '업데이트 완료' : status === 'downloading' ? '업데이트 중' : '업데이트 알림'}
            </span>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="p-2 -m-1.5 rounded-lg hover:bg-black/10 transition-colors shrink-0 text-gray-400 hover:text-gray-600"
            title="닫기"
          >
            <X size={14} />
          </button>
        </div>

        <div className="text-sm text-gray-600">
          {status === 'available' && (
            <>
              <p className="mb-3">새 버전 <span className="font-semibold text-brand-600">v{version}</span>이(가) 있습니다.</p>
              <button
                onClick={handleInstall}
                className="w-full px-3 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 transition-colors"
              >
                지금 설치
              </button>
            </>
          )}

          {status === 'downloading' && (
            <p>업데이트를 다운로드하고 설치하는 중...</p>
          )}

          {status === 'ready' && (
            <p className="text-green-700">설치 완료, 재시작 중...</p>
          )}

          {status === 'error' && (
            <p className="text-brand-600">{errorMessage}</p>
          )}
        </div>
      </div>
    </div>
  );
};
